import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiError, LoginResult } from '../../../core/auth/auth.models';
import { AuthService } from '../../../core/auth/auth.service';

/**
 * Porta única de entrada. Não sabe (nem pergunta) a que empresa o utilizador pertence:
 * o backend resolve isso pelo e-mail e diz, na resposta, para onde ir.
 */
@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './login.html',
})
export class Login implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  /** Veio de uma página protegida: depois de entrar, volta para lá. */
  protected readonly returning = signal(!!this.route.snapshot.queryParamMap.get('returnUrl'));

  /** Quem já tem sessão não precisa de ver o formulário — vai direto para onde estava a ir. */
  ngOnInit(): void {
    // O endereço muda sem a página ser recriada (o Voltar depois de sair): o aviso acompanha-o.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) =>
      this.returning.set(!!params.get('returnUrl')));
    const tenant = this.auth.currentTenant();
    if (tenant) {
      this.goTenant(tenant);
      return;
    }
    if (this.auth.platformSession()) {
      this.router.navigate(['/platform']);
    }
  }

  protected readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });
  protected readonly loading = signal(false);
  protected readonly serverError = signal<string | null>(null);

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.serverError.set(null);
    const { email, password } = this.form.getRawValue();
    this.auth.login(email, password).subscribe({
      next: (result) => (result.scope === 'PLATFORM' || !result.tenant
        ? this.router.navigate(this.destination(result))
        : this.goTenant(result.tenant)),
      error: (error: HttpErrorResponse) => {
        this.loading.set(false);
        this.serverError.set((error.error as ApiError)?.message ?? 'Não foi possível entrar.');
      },
    });
  }

  /** O endereço de onde se veio, só se for desta empresa (nunca um endereço de fora). */
  private goTenant(tenant: string): void {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? '';
    if (returnUrl.startsWith(`/${tenant}/`) && !returnUrl.startsWith('//')) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(['/', tenant, 'ocorrencias']);
    }
  }

  /** Super-admin vai para a plataforma; utilizador de empresa vai para as ocorrências dela. */
  private destination(result: LoginResult): unknown[] {
    return result.scope === 'PLATFORM' ? ['/platform'] : ['/', result.tenant, 'ocorrencias'];
  }
}
